import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, model, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TimeSlotGroup, TimeSlotOption } from '@checkout-v2/const';
import { SdvorInputComponent } from '@sdvor/shared/ui/v2';
import { NgxMaskDirective } from 'ngx-mask';

/** Преобразует время в формате "HH:mm" в количество минут от начала суток. */
function timeToMinutes(time: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time);

  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (hours > 23 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

/**
 * Проверяет, что введённое время находится в допустимом диапазоне
 * [timeFrom, timeTo] выбранной группы слотов.
 */
function timeInRangeValidator(getGroup: () => TimeSlotGroup): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string | null;

    if (!value) {
      return null;
    }

    const clientInterval = getGroup()?.options?.[0]?.interval?.clientInterval;

    if (!clientInterval) {
      return null;
    }

    const current = timeToMinutes(value);
    const from = timeToMinutes(clientInterval.timeFrom.slice(0, 5));
    const to = timeToMinutes(clientInterval.timeTo.slice(0, 5));

    if (current === null) {
      return { timeInvalid: true };
    }

    if (from === null || to === null) {
      return null;
    }

    if (current < from || current > to) {
      return { timeOutOfRange: { from: clientInterval.timeFrom, to: clientInterval.timeTo } };
    }

    return null;
  };
}

@Component({
  selector: 'sd-exactly-in-time',
  imports: [ReactiveFormsModule, SdvorInputComponent, NgxMaskDirective],
  templateUrl: './exactly-in-time.component.html',
  styleUrl: './exactly-in-time.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExactlyInTimeComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectedGroup = input.required<TimeSlotGroup>();
  readonly selectedOption = model.required<TimeSlotOption | undefined>();

  protected readonly control = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(4), timeInRangeValidator(() => this.selectedGroup())],
    updateOn: 'blur',
  });

  constructor() {
    // Перевалидируем контрол при смене выбранной группы,
    // т.к. с updateOn: 'blur' автоматически это не произойдёт.
    effect(() => {
      this.selectedGroup();
      this.control.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    /**
     * Если пользователь ввел валидное значение то выбираем опцию, в противном случае отменяем выбор
     */
    this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((customInterval) => {
      const firstOption = this.selectedGroup().options[0];
      const control = this.control;

      if (control.valid && customInterval) {
        this.selectedOption.set(this.replaceOptionWithCustomInterval(firstOption, customInterval));
      } else {
        this.selectedOption.set(undefined);
      }
    });
  }

  /**
   * Подменяем интервал у опции на введенное пользователем значение
   */
  replaceOptionWithCustomInterval(option: TimeSlotOption, timeFromTo: string): TimeSlotOption {
    return {
      ...option,
      interval: {
        ...option.interval,
        clientInterval: {
          ...option.interval.clientInterval,
          timeFromTo,
        },
      },
    };
  }
}
